<script lang="ts" module>
  // Zeitraum-Picker im Grafana-Style. Ein Button zeigt den aktiven
  // Zeitraum, Klick oeffnet eine Popover-Liste mit 12 Presets + einer
  // Custom-Sektion (datetime-local from/to).
  //
  // Verwendung:
  //   <TimeRangePicker bind:value={range} />
  //
  // Der gebundene Wert ist ein TimeRange-Objekt mit absoluten ms-
  // Grenzen + Label. Bei Presets enthaelt `preset_hours` zusaetzlich
  // die Stunden-Anzahl, damit Endpoints die immer noch `hours=N` als
  // Query-Param erwarten direkt damit weiterarbeiten koennen (Energy
  // /climate-Stack). Custom-Ranges haben `preset_hours = undefined`.

  export interface TimeRange {
    from_ms: number;
    to_ms: number;
    label: string;
    // Wenn gesetzt, ist es einer der Presets -- Konvertierung in
    // hours-basierte Endpoints ist verlustfrei (to_ms = jetzt).
    preset_hours?: number;
  }

  interface Preset {
    hours: number;
    label: string;
  }

  // 12 Presets, gleiche Reihenfolge wie der Grafana-Standard.
  export const PRESETS: Preset[] = [
    { hours: 1 / 12, label: "Letzte 5 Min" },
    { hours: 0.25, label: "Letzte 15 Min" },
    { hours: 0.5, label: "Letzte 30 Min" },
    { hours: 1, label: "Letzte 1 h" },
    { hours: 3, label: "Letzte 3 h" },
    { hours: 6, label: "Letzte 6 h" },
    { hours: 12, label: "Letzte 12 h" },
    { hours: 24, label: "Letzte 24 h" },
    { hours: 48, label: "Letzte 2 Tage" },
    { hours: 168, label: "Letzte 7 Tage" },
    { hours: 720, label: "Letzte 30 Tage" },
    { hours: 2160, label: "Letzte 90 Tage" },
  ];

  // Helper fuer Konsumenten: TimeRange aus Preset bauen.
  export function rangeFromPreset(hours: number, label?: string): TimeRange {
    const now = Date.now();
    const p = PRESETS.find((x) => x.hours === hours);
    return {
      from_ms: now - hours * 3600_000,
      to_ms: now,
      label: label ?? p?.label ?? `Letzte ${hours} h`,
      preset_hours: hours,
    };
  }

  // rangeToQuery baut den Query-String fuer Backend-Endpoints, die
  // parseTimeRange erwarten (climate/history, climate/events, energy/
  // history, ...). Bei Presets schicken wir `hours=N` damit die alten
  // Caller-Pfade gleich aussehen; bei Custom-Ranges `from_ms=&to_ms=`.
  // Rueckgabe enthaelt den fuehrenden '?', oder leer wenn range null.
  export function rangeToQuery(r: TimeRange | undefined | null): string {
    if (!r) return "";
    if (r.preset_hours != null) {
      return `?hours=${r.preset_hours}`;
    }
    return `?from_ms=${r.from_ms}&to_ms=${r.to_ms}`;
  }
</script>

<script lang="ts">
  import { onDestroy } from "svelte";

  interface Props {
    value: TimeRange;
  }

  let { value = $bindable() }: Props = $props();

  let open = $state(false);
  let popover = $state<HTMLDivElement | undefined>(undefined);

  // Custom-Inputs als datetime-local-Strings ("YYYY-MM-DDTHH:mm").
  // Werden erst beim "Anwenden"-Klick in value uebersetzt -- waehrend
  // der User tippt soll sich der aktive Bereich nicht jedesmal aendern.
  let customFromStr = $state("");
  let customToStr = $state("");

  function fmtForInput(ms: number): string {
    const d = new Date(ms);
    const pad = (n: number) => String(n).padStart(2, "0");
    return (
      `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
      `T${pad(d.getHours())}:${pad(d.getMinutes())}`
    );
  }

  function pickPreset(p: Preset) {
    value = rangeFromPreset(p.hours, p.label);
    open = false;
  }

  function applyCustom() {
    const from = Date.parse(customFromStr);
    const to = Date.parse(customToStr);
    if (Number.isNaN(from) || Number.isNaN(to) || from >= to) return;
    value = {
      from_ms: from,
      to_ms: to,
      label: `${customFromStr.replace("T", " ")} - ${customToStr.replace("T", " ")}`,
    };
    open = false;
  }

  function toggle() {
    if (!open) {
      // Custom-Inputs mit aktuellem Range vorbelegen, damit der User
      // einen sinnvollen Startpunkt zum Tippen hat.
      customFromStr = fmtForInput(value.from_ms);
      customToStr = fmtForInput(value.to_ms);
    }
    open = !open;
  }

  function onDocClick(e: MouseEvent) {
    if (!open || !popover) return;
    const target = e.target as Node;
    if (popover.contains(target)) return;
    open = false;
  }
  function onEsc(e: KeyboardEvent) {
    if (e.key === "Escape") open = false;
  }
  $effect(() => {
    if (!open) return;
    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onEsc);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onEsc);
    };
  });

  onDestroy(() => {
    document.removeEventListener("mousedown", onDocClick);
    document.removeEventListener("keydown", onEsc);
  });

  const activeHours = $derived(value.preset_hours);
</script>

<div class="relative inline-block" bind:this={popover}>
  <button
    type="button"
    onclick={toggle}
    aria-haspopup="dialog"
    aria-expanded={open}
    class="border-border surface hover:border-accent/60 rounded-md border px-3 py-1 text-xs font-medium tabular-nums transition"
  >
    {value.label}
    <span class="text-fg-subtle ml-1">▾</span>
  </button>

  {#if open}
    <div
      class="border-border surface absolute right-0 z-50 mt-1 grid w-56 max-w-[calc(100vw-1.5rem)] gap-1 rounded-lg border p-2 shadow-lg"
      role="dialog"
    >
      <div class="grid grid-cols-1 gap-0.5">
        {#each PRESETS as p}
          {@const active = activeHours === p.hours}
          <button
            type="button"
            onclick={() => pickPreset(p)}
            class="rounded ctrl-s text-left text-xs transition {active
              ? 'bg-accent/15 text-accent'
              : 'text-fg-muted hover:bg-bg-muted hover:text-fg'}"
          >
            {p.label}
          </button>
        {/each}
      </div>

      <div class="border-border mt-1 grid gap-2 border-t pt-2">
        <div class="text-fg-subtle t-eyebrow">Eigener Zeitraum</div>
        <label class="grid gap-1 t-meta">
          <span class="text-fg-muted">Von</span>
          <input
            type="datetime-local"
            bind:value={customFromStr}
            class="border-border surface focus:border-accent rounded-md border ctrl-s text-xs outline-none focus:ring-2 focus:ring-accent/20"
          />
        </label>
        <label class="grid gap-1 t-meta">
          <span class="text-fg-muted">Bis</span>
          <input
            type="datetime-local"
            bind:value={customToStr}
            class="border-border surface focus:border-accent rounded-md border ctrl-s text-xs outline-none focus:ring-2 focus:ring-accent/20"
          />
        </label>
        <button
          type="button"
          onclick={applyCustom}
          class="bg-accent text-accent-fg justify-self-end rounded-md px-3 py-1 text-xs font-medium transition disabled:opacity-50"
        >
          Anwenden
        </button>
      </div>
    </div>
  {/if}
</div>
